import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Megaphone, Save } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";

const EMPTY = {
  enabled: false,
  message: "",
  link_url: "",
  link_label: "",
  bg_color: "#4ADE80",
  text_color: "#0F0F10",
};

export default function AnnouncementTab() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/announcement");
      setForm({ ...EMPTY, ...data });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/announcement", form);
      toast.success("Announcement saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#4ADE80]" /></div>;

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="border border-white/10 bg-[#1F1F1F] p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Megaphone className="h-5 w-5 text-[#4ADE80]" />
            <span className="font-display text-lg">Site-wide banner</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-widest text-[#A8A8A8]">{form.enabled ? "Live" : "Off"}</span>
            <Switch
              data-testid="announcement-enabled-toggle"
              checked={!!form.enabled}
              onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
            />
          </div>
        </div>

        <div>
          <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Message</label>
          <Input
            data-testid="announcement-message-input"
            value={form.message}
            onChange={set("message")}
            placeholder="Free shipping on orders over $150"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Link URL (optional)</label>
            <Input
              data-testid="announcement-link-url"
              value={form.link_url || ""}
              onChange={set("link_url")}
              placeholder="/products"
            />
          </div>
          <div>
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Link Label</label>
            <Input
              data-testid="announcement-link-label"
              value={form.link_label || ""}
              onChange={set("link_label")}
              placeholder="Shop now"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Background Color</label>
            <div className="flex items-center gap-2">
              <Input data-testid="announcement-bg-color" value={form.bg_color} onChange={set("bg_color")} className="font-mono" />
              <div className="h-9 w-9 rounded border border-white/20" style={{ background: form.bg_color }} />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Text Color</label>
            <div className="flex items-center gap-2">
              <Input data-testid="announcement-text-color" value={form.text_color} onChange={set("text_color")} className="font-mono" />
              <div className="h-9 w-9 rounded border border-white/20" style={{ background: form.text_color }} />
            </div>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div>
        <div className="text-xs text-[#A8A8A8] uppercase tracking-widest mb-2">Preview</div>
        <div
          data-testid="announcement-preview"
          style={{ background: form.bg_color, color: form.text_color }}
          className="rounded text-center text-[13px] font-medium py-2.5 px-10"
        >
          {form.message || <span className="opacity-50">Your announcement will appear here…</span>}
          {form.link_url && form.link_label && (
            <span className="ml-3 underline">{form.link_label} →</span>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          data-testid="announcement-save-btn"
          onClick={save}
          disabled={saving}
          className="bg-[#4ADE80] text-[#0F0F10] hover:bg-[#6FE998]"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save & Publish
        </Button>
      </div>
    </div>
  );
}
